/**
 * CommentThreadCard — render compartido de un thread de comments.
 * (Doc 21 Fase 5)
 *
 * Lo consumen tanto:
 *   - `CommentsPanel` (editor mode, panel persistente lateral)
 *   - `CommentsViewerPanel` (viewer mode)
 *
 * Renderiza el root + replies + eventos status_change cronológicos,
 * con thread colapsable cuando hay muchas replies y action buttons
 * según permisos por rol del actor.
 *
 * Cada acción se delega vía callbacks — el caller decide qué hacer
 * (mutation, optimistic update, etc.). Esto mantiene el componente
 * dumb y testeable.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import type { TFunction } from 'i18next'
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Edit2,
  Loader2,
  MessageSquare,
  RotateCcw,
  Send,
  Sparkles,
  ThumbsUp,
  Trash2,
} from 'lucide-react'
import { marked } from 'marked'

import type {
  ArtifactComment,
  CommentStatus,
} from '../../../api/comments'
import { toIntlLocale } from '../../../i18n/config'
import { diffLines, summarizeDiff } from '../../../lib/lineDiff'
import { ConfirmDialog } from '../../ConfirmDialog'
import { valueToReadableText } from './commentAnchorText'

export interface CommentThreadGroup {
  root: ArtifactComment
  /** Children = replies (kind='reply') + status_change events. Mezclados
   *  cronológicamente como vienen del backend. */
  children: ArtifactComment[]
}

export interface CommentThreadCardProps {
  thread: CommentThreadGroup
  /** ID del usuario actual — para badge "Tú" + decidir qué actions
   *  mostrar. Null si no hay user (raro: el panel no se monta sin
   *  auth). */
  currentUserId: string | null
  /** ID del autor del artifact — necesario para mostrar la action
   *  "Marcar como subsanado" solo a esa persona. */
  artifactAuthorId: string | null
  /** Display names resueltos (member.user_id → display_name). */
  displayNameByUserId: Map<string, string>
  /** Para el header del thread con la sección anclada. Map del
   *  template i18n. */
  sectionTitle: string
  /** Doc 21 Fase 10 — valor vivo de la sección anclada al comment,
   *  resuelto por el panel via `parseInstanceKey` + `getValueAtPath`
   *  sobre `version.content`. Cuando difiere del `section_snapshot`,
   *  el `CommentDiffBlock` muestra el diff line-based entre ambos
   *  (mismo estilo visual que el modal "Subsanar con IA"). Si el
   *  caller no lo pasa (contextos donde no aplica, p.ej. historial),
   *  el bloque sigue renderizando solo el snapshot como antes. */
  liveSectionValue?: unknown
  /** Estados de mutation para deshabilitar botones durante la
   *  request. */
  isReplying?: boolean
  isStatusChanging?: boolean
  isEditing?: boolean
  isDeleting?: boolean
  /** Si false, oculta acciones de edit/delete del root y replies
   *  propias. Útil cuando el card vive en un contexto read-only
   *  (ej. histórico). */
  showEditDeleteActions?: boolean
  /** Si false, oculta los buttons de status transition. Útil para
   *  contextos read-only o cuando el thread vive en una versión
   *  que ya no es editable. */
  showStatusActions?: boolean
  /** Callbacks de las actions. Si no se pasa una callback (o se pasa
   *  `undefined` explícito — OT-0009 M4 gatea así según `canComment`),
   *  el button asociado no se muestra. */
  onReply?: ((body: string) => void) | undefined
  onMarkAddressed?: ((note?: string) => void) | undefined
  onResolve?: (() => void) | undefined
  onReopen?: (() => void) | undefined
  onEditOwn?: ((commentId: string, newBody: string) => void) | undefined
  onDeleteOwn?: ((commentId: string) => void) | undefined
  /** Abre la asistencia de IA ("Subsanar con IA") sobre el comentario.
   *  Sin callback no hay botón: el caller lo pasa solo a quien puede
   *  editar el artefacto — dueño actual ∪ líder ∪ admin, espejo de
   *  `load_artifact_for_edit` en generar/aplicar/descartar (OT-0016). No
   *  depende de quién creó el artefacto. */
  onAiAssist?: (() => void) | undefined
}

export function CommentThreadCard({
  thread,
  currentUserId,
  artifactAuthorId,
  displayNameByUserId,
  sectionTitle,
  liveSectionValue,
  isReplying = false,
  isStatusChanging = false,
  isDeleting = false,
  showEditDeleteActions = false,
  showStatusActions = true,
  onReply,
  onMarkAddressed,
  onResolve,
  onReopen,
  onEditOwn,
  onDeleteOwn,
  onAiAssist,
}: CommentThreadCardProps) {
  const { t } = useTranslation('projects')
  const { root, children } = thread

  const isCommenter =
    currentUserId !== null && root.author_user_id === currentUserId
  const isArtifactAuthor =
    currentUserId !== null && artifactAuthorId === currentUserId

  // Collapse de replies viejas si > 5 (D5/Fase 5).
  const COLLAPSE_THRESHOLD = 5
  const KEEP_VISIBLE = 2
  const [expanded, setExpanded] = useState(false)
  const totalChildren = children.length
  const shouldCollapse = totalChildren > COLLAPSE_THRESHOLD
  const visibleChildren =
    shouldCollapse && !expanded
      ? children.slice(totalChildren - KEEP_VISIBLE)
      : children
  const hiddenCount = shouldCollapse && !expanded
    ? totalChildren - KEEP_VISIBLE
    : 0

  // Reply form state.
  const [replyBody, setReplyBody] = useState('')
  const [showReplyForm, setShowReplyForm] = useState(false)

  // Edit own root state.
  const [editingRootBody, setEditingRootBody] = useState<string | null>(null)
  const editingActive = editingRootBody !== null

  // Delete confirm con design system (no window.confirm nativo).
  const [confirmDelete, setConfirmDelete] = useState(false)

  function handleSubmitReply() {
    const trimmed = replyBody.trim()
    if (!trimmed || !onReply) return
    onReply(trimmed)
    setReplyBody('')
    setShowReplyForm(false)
  }

  function handleSubmitEdit() {
    if (editingRootBody === null || !onEditOwn) return
    const trimmed = editingRootBody.trim()
    if (!trimmed) return
    onEditOwn(root.id, trimmed)
    setEditingRootBody(null)
  }

  const status = root.status

  return (
    <article className="px-4 py-4">
      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
          {sectionTitle}
        </span>
        <StatusChip status={status} />
      </div>

      {/* Root comment.
          Orden visual: autor → COMENTARIO (destacado) → contexto
          (excerpt + snapshot/diff). El comentario del usuario es el
          foco — el contexto es secundario. Antes el body iba después
          del CommentDiffBlock y se perdía visualmente porque el
          bloque de comparación es grande. Patrón inspirado en
          GitHub PR comments / Google Docs comments: la palabra del
          autor primero, evidencia anclada después. */}
      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-medium text-ink truncate">
              {root.author_user_id
                ? displayNameByUserId.get(root.author_user_id) ??
                  t('comments_panel.unknown_user')
                : t('comments_panel.unknown_user')}
            </span>
            {isCommenter ? (
              <span className="px-1.5 py-0.5 rounded bg-brand-soft text-[9px] uppercase tracking-wider text-brand">
                {t('comments_panel.you')}
              </span>
            ) : null}
            <span className="text-[10px] text-ink-subtle">
              {formatRelativeOrAbs(root.created_at)}
            </span>
            {root.updated_at !== root.created_at ? (
              <span className="text-[9px] uppercase text-ink-subtle italic">
                {t('comments_drawer.edited')}
              </span>
            ) : null}
          </div>
          {showEditDeleteActions &&
          isCommenter &&
          !editingActive &&
          (onEditOwn || onDeleteOwn) ? (
            <div className="flex items-center gap-0.5">
              {onEditOwn ? (
                <button
                  type="button"
                  onClick={() => setEditingRootBody(root.body)}
                  className="p-1 rounded text-ink-subtle hover:text-ink hover:bg-surface-muted"
                  aria-label={t('comments_drawer.edit')}
                  title={t('comments_drawer.edit')}
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              ) : null}
              {onDeleteOwn ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={isDeleting}
                  className="p-1 rounded text-ink-subtle hover:text-critical hover:bg-critical-soft disabled:opacity-50"
                  aria-label={t('comments_drawer.delete')}
                  title={t('comments_drawer.delete')}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Cuerpo del comentario o textarea de edición — slot
            destacado con border-left brand. */}
        {editingActive ? (
          <div className="space-y-1.5 mb-3">
            <textarea
              value={editingRootBody ?? ''}
              onChange={(e) => setEditingRootBody(e.target.value)}
              rows={3}
              maxLength={5000}
              className="w-full bg-canvas border border-line rounded px-2 py-1.5 text-sm focus:outline-none focus:border-brand"
            />
            <div className="flex items-center gap-1 justify-end">
              <button
                type="button"
                onClick={() => setEditingRootBody(null)}
                className="px-2 py-1 text-[11px] text-ink-muted hover:text-ink rounded"
              >
                {t('comments_drawer.cancel_edit')}
              </button>
              <button
                type="button"
                onClick={handleSubmitEdit}
                disabled={!(editingRootBody ?? '').trim()}
                className="inline-flex items-center gap-1 px-2 py-1 bg-brand hover:bg-brand-deep text-canvas text-[11px] rounded disabled:opacity-50"
              >
                {t('comments_drawer.save_edit')}
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-3 rounded-md border-l-4 border-brand bg-brand-soft/30 px-3 py-2.5">
            <p className="text-sm text-ink whitespace-pre-wrap break-words leading-relaxed">
              {root.body}
            </p>
          </div>
        )}

        {/* Contexto anclado al comentario (excerpt + snapshot/diff).
            Va debajo del cuerpo porque es secundario — el lector ya
            sabe qué dijo el autor; el snapshot explica sobre qué. */}
        <CommentDiffBlock
          excerpt={root.excerpt}
          sectionSnapshot={root.section_snapshot}
          liveSectionValue={liveSectionValue}
          t={t}
        />
      </div>

      {/* Replies + status_changes */}
      {visibleChildren.length > 0 || hiddenCount > 0 ? (
        <ul className="mt-3 ml-4 pl-3 border-l-2 border-line space-y-2">
          {hiddenCount > 0 ? (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-[11px] text-brand hover:underline inline-flex items-center gap-1"
              >
                <ChevronDown className="w-3 h-3" />
                {t('comments_panel.thread.show_more', {
                  count: hiddenCount,
                })}
              </button>
            </li>
          ) : null}
          {expanded && shouldCollapse ? (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-[11px] text-ink-muted hover:text-ink inline-flex items-center gap-1"
              >
                <ChevronUp className="w-3 h-3" />
                {t('comments_panel.thread.collapse')}
              </button>
            </li>
          ) : null}
          {visibleChildren.map((child) => (
            <li key={child.id}>
              <ChildItem
                child={child}
                isMine={
                  currentUserId !== null &&
                  child.author_user_id === currentUserId
                }
                displayName={
                  child.author_user_id
                    ? displayNameByUserId.get(child.author_user_id) ??
                      t('comments_panel.unknown_user')
                    : t('comments_panel.unknown_user')
                }
              />
            </li>
          ))}
        </ul>
      ) : null}

      {/* Action footer */}
      {showStatusActions ||
      onReply !== undefined ||
      onAiAssist !== undefined ? (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {/* Reply (cualquier member). Se muestra si onReply está definido
              y el root no está resolved. */}
          {onReply && status !== 'resolved' && !showReplyForm ? (
            <button
              type="button"
              onClick={() => setShowReplyForm(true)}
              disabled={isReplying || isStatusChanging}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-line text-[11px] text-ink hover:border-line-strong hover:bg-surface-muted disabled:opacity-50"
            >
              <MessageSquare className="w-3 h-3" />
              {t('comments_panel.actions.reply')}
            </button>
          ) : null}

          {showStatusActions &&
          isArtifactAuthor &&
          status === 'pending' &&
          onMarkAddressed ? (
            <button
              type="button"
              onClick={() => onMarkAddressed()}
              disabled={isReplying || isStatusChanging}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-brand text-canvas text-[11px] hover:bg-brand-deep disabled:opacity-50"
            >
              {isStatusChanging ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <ThumbsUp className="w-3 h-3" />
              )}
              {t('comments_panel.actions.mark_addressed')}
            </button>
          ) : null}

          {showStatusActions &&
          isCommenter &&
          (status === 'pending' || status === 'addressed') &&
          onResolve ? (
            <button
              type="button"
              onClick={onResolve}
              disabled={isReplying || isStatusChanging}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-official text-canvas text-[11px] hover:bg-official-deep disabled:opacity-50"
            >
              {isStatusChanging ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Check className="w-3 h-3" />
              )}
              {t('comments_panel.actions.resolve')}
            </button>
          ) : null}

          {showStatusActions && status === 'resolved' && onReopen ? (
            <button
              type="button"
              onClick={onReopen}
              disabled={isReplying || isStatusChanging}
              title={t('comments_panel.actions.reopen_hint')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-line text-[11px] text-ink-muted hover:text-ink hover:border-line-strong disabled:opacity-50"
            >
              <RotateCcw className="w-3 h-3" />
              {t('comments_panel.actions.reopen')}
            </button>
          ) : null}

          {onAiAssist && status !== 'resolved' ? (
            <button
              type="button"
              onClick={onAiAssist}
              disabled={isReplying || isStatusChanging}
              title={t('comments_panel.actions.ai_assist_hint')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-brand/40 text-[11px] text-brand hover:bg-brand-soft disabled:opacity-50"
            >
              <Sparkles className="w-3 h-3" />
              {t('comments_panel.actions.ai_assist')}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Reply form */}
      {showReplyForm ? (
        <div className="mt-3 space-y-1.5">
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            rows={2}
            maxLength={5000}
            placeholder={t('comments_panel.reply_placeholder')}
            disabled={isReplying}
            className="w-full bg-canvas border border-line rounded px-2 py-1.5 text-xs focus:outline-none focus:border-brand disabled:opacity-50"
          />
          <div className="flex items-center gap-1.5 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowReplyForm(false)
                setReplyBody('')
              }}
              disabled={isReplying}
              className="px-2 py-1 text-[11px] text-ink-muted hover:text-ink rounded disabled:opacity-50"
            >
              {t('comments_panel.actions.cancel_reply')}
            </button>
            <button
              type="button"
              onClick={handleSubmitReply}
              disabled={isReplying || !replyBody.trim()}
              className="inline-flex items-center gap-1 px-2 py-1 bg-brand hover:bg-brand-deep text-canvas text-[11px] rounded disabled:opacity-50"
            >
              {isReplying ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              {t('comments_panel.actions.send_reply')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Delete confirm con design system. Reemplaza window.confirm. */}
      <ConfirmDialog
        open={confirmDelete}
        tone="danger"
        title={t('comments_drawer.delete')}
        body={t('comments_drawer.confirm_delete')}
        confirmLabel={t('comments_drawer.delete')}
        cancelLabel={t('comments_drawer.cancel_edit')}
        busy={isDeleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (onDeleteOwn) onDeleteOwn(root.id)
          setConfirmDelete(false)
        }}
      />
    </article>
  )
}

// ─── ChildItem (reply o status_change) ──────────────────────────────────────
function ChildItem({
  child,
  isMine,
  displayName,
}: {
  child: ArtifactComment
  isMine: boolean
  displayName: string
}) {
  const { t } = useTranslation('projects')

  if (child.kind === 'status_change') {
    const payload = (child.system_payload ?? {}) as {
      from?: string
      to?: string
      note?: string | null
    }
    return (
      <div className="text-[11px]">
        <div className="flex items-center gap-1.5 text-ink-muted flex-wrap">
          <ArrowRight className="w-3 h-3" />
          <span className="font-medium">{displayName}</span>
          <span>
            {t('comments_panel.thread.status_event', {
              from: payload.from ?? '—',
              to: payload.to ?? '—',
            })}
          </span>
          <span className="text-ink-subtle">
            {formatRelativeOrAbs(child.created_at)}
          </span>
        </div>
        {payload.note ? (
          <p className="mt-1 ml-4 text-ink whitespace-pre-wrap break-words">
            {payload.note}
          </p>
        ) : null}
      </div>
    )
  }

  // kind === 'reply'
  return (
    <div className="text-[11px]">
      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
        <span className="font-medium text-ink">{displayName}</span>
        {isMine ? (
          <span className="px-1 py-0 rounded bg-brand-soft text-[9px] uppercase tracking-wider text-brand">
            {t('comments_panel.you')}
          </span>
        ) : null}
        <span className="text-ink-subtle">
          {formatRelativeOrAbs(child.created_at)}
        </span>
      </div>
      <p className="text-ink whitespace-pre-wrap break-words">{child.body}</p>
    </div>
  )
}

// ─── CommentDiffBlock (Doc 21 Fase 6 + Fase 10 — evidencia + comparación)
/**
 * Renderiza los bloques de evidencia frozen al momento de comentar y,
 * cuando aplica (Doc 21 Fase 10), la comparación con el texto vivo.
 *
 * Layout:
 *
 *   · "Texto cuando comenté" — el snippet que el reviewer seleccionó
 *     (campo `excerpt`). Render plano italic gris.
 *   · "Versión previa de la sección" — el valor COMPLETO de la sección
 *     capturado por el backend al crear el comment (`section_snapshot`).
 *     Pasa por `valueToReadableText` (TipTap doc, listas, KV, strings) →
 *     markdown → marked.parse → HTML con prose-hiva-compact.
 *
 *     Cuando recibimos `liveSectionValue` (resuelto por el padre via
 *     parseInstanceKey + getValueAtPath sobre `version.content`) y el
 *     markdown vivo difiere del snapshot, el header del bloque cambia
 *     a "Comparación con el texto actual" con stats +N −M, y un
 *     toggle "Ver original" alterna entre comparación y snapshot puro.
 *     El diff es line-based (mismo `diffLines` que usa el modal
 *     "Subsanar con IA"), mismo estilo visual (verde-soft add /
 *     rojo-soft del con line-through, font-mono).
 *
 * Reglas de display:
 *   · Excerpt presente y snapshot null (legacy pre-Fase 6) → solo
 *     bloque 1, sin label (compatibilidad).
 *   · Excerpt presente y snapshot presente → ambos bloques con labels.
 *   · Excerpt ausente y snapshot presente → solo bloque 2 con label.
 *   · Ambos ausentes → no renderiza nada.
 *
 * Collapse: el conjunto se colapsa con fade overlay cuando el contenido
 * excede heurística (~300 chars o >4 saltos). Toggle único para ambos.
 * En modo "Comparación" no aplica collapse — el diff lleva su propio
 * `max-h-72 overflow-y-auto` (igual que en el modal IA).
 */
function CommentDiffBlock({
  excerpt,
  sectionSnapshot,
  liveSectionValue,
  t,
}: {
  excerpt: string | null
  sectionSnapshot: unknown
  liveSectionValue?: unknown
  t: TFunction
}) {
  const [expanded, setExpanded] = useState(false)
  // Doc 21 Fase 10 — toggle de la vista del bloque snapshot. Default
  // 'diff' cuando hay diferencias (el caso que importa al reviewer);
  // 'original' cuando el caller quiere ver el snapshot puro.
  const [snapshotView, setSnapshotView] = useState<'diff' | 'original'>('diff')

  const normalizedExcerpt = (excerpt ?? '').trim()
  const hasExcerpt = normalizedExcerpt.length > 0

  // Snapshot → markdown (valueToReadableText maneja todos los shapes:
  // TipTap doc, lista, KV, string). Vacío/null → omitimos el bloque.
  const snapshotMarkdown = useMemo(() => {
    return valueToReadableText(sectionSnapshot)
  }, [sectionSnapshot])

  const snapshotHtml = useMemo(() => {
    if (!snapshotMarkdown) return ''
    try {
      return marked.parse(snapshotMarkdown, { async: false }) as string
    } catch {
      return ''
    }
  }, [snapshotMarkdown])

  const hasSnapshot = snapshotHtml.length > 0

  // Doc 21 Fase 10 — markdown del valor VIVO de la sección anclada.
  // El padre resuelve liveSectionValue contra `version.content` con
  // el mismo encoding del backend. Si no llega (panel histórico, etc.)
  // o si el shape es no-textual, devuelve null y la comparación se
  // omite (caemos al render original del snapshot).
  const liveMarkdown = useMemo(() => {
    if (liveSectionValue === undefined) return null
    return valueToReadableText(liveSectionValue)
  }, [liveSectionValue])

  const diff = useMemo(() => {
    if (!snapshotMarkdown || liveMarkdown === null) return null
    if (snapshotMarkdown === liveMarkdown) return null
    return diffLines(snapshotMarkdown, liveMarkdown)
  }, [snapshotMarkdown, liveMarkdown])

  const diffStats = useMemo(
    () => (diff ? summarizeDiff(diff) : null),
    [diff],
  )

  const hasChanges = diff !== null && diffStats !== null &&
    (diffStats.added > 0 || diffStats.removed > 0)

  // Nada que mostrar.
  if (!hasExcerpt && !hasSnapshot) return null

  // Legacy: excerpt sin snapshot (comments creados antes de Fase 6 o
  // antes de implementar la captura). Render plano del excerpt, igual
  // que estaba antes — no rompemos el viewer de comments viejos.
  if (hasExcerpt && !hasSnapshot) {
    return (
      <blockquote className="my-2 pl-2 border-l-2 border-brand/40 text-[11px] italic text-ink-muted whitespace-pre-wrap break-words">
        {excerpt}
      </blockquote>
    )
  }

  // Heurística "es largo" → activa collapse/expand del excerpt y el
  // snapshot puro. La vista 'diff' usa su propio scroll interno.
  const isLong =
    normalizedExcerpt.length > 300 ||
    (snapshotMarkdown?.length ?? 0) > 300 ||
    (normalizedExcerpt.match(/\n/g)?.length ?? 0) > 4 ||
    (snapshotMarkdown?.match(/\n/g)?.length ?? 0) > 4 ||
    (snapshotMarkdown?.match(/##\s/g)?.length ?? 0) > 1

  const collapsedClass = isLong && !expanded
    ? 'relative max-h-32 overflow-hidden'
    : ''

  const showingDiff = hasChanges && snapshotView === 'diff'

  return (
    <div className="my-2 space-y-2">
      {hasExcerpt ? (
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-ink-subtle mb-1">
            {t('comments_thread.diff.excerpt_label')}
          </p>
          <div className={collapsedClass}>
            <blockquote className="pl-2 border-l-2 border-line text-[11px] italic text-ink-subtle whitespace-pre-wrap break-words">
              {excerpt}
            </blockquote>
            {isLong && !expanded ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface to-transparent"
              />
            ) : null}
          </div>
        </div>
      ) : null}
      {hasSnapshot ? (
        <div>
          <div className="mb-1 flex items-baseline gap-2 flex-wrap">
            <p
              className={
                hasChanges
                  ? 'text-[10px] font-medium uppercase tracking-wider text-brand'
                  : 'text-[10px] font-medium uppercase tracking-wider text-brand'
              }
            >
              {showingDiff
                ? t('comments_thread.diff.compare_label')
                : t('comments_thread.diff.snapshot_label')}
            </p>
            {showingDiff && diffStats ? (
              <span className="font-mono text-[10px] text-ink-subtle">
                <span className="text-official">+{diffStats.added}</span>
                {' '}
                <span className="text-critical">−{diffStats.removed}</span>
              </span>
            ) : null}
            {hasChanges ? (
              <button
                type="button"
                onClick={() =>
                  setSnapshotView((v) =>
                    v === 'diff' ? 'original' : 'diff',
                  )
                }
                className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-brand hover:underline"
              >
                {showingDiff
                  ? t('comments_thread.diff.view_original')
                  : t('comments_thread.diff.view_comparison')}
              </button>
            ) : liveMarkdown !== null && !hasChanges ? (
              <span className="ml-auto text-[10px] italic text-ink-subtle">
                {t('comments_thread.diff.no_changes')}
              </span>
            ) : null}
          </div>
          {showingDiff && diff ? (
            <SnapshotDiffView diff={diff} />
          ) : (
            <div
              className={`${collapsedClass} border-l-2 border-brand/60 pl-2`}
            >
              <div
                className="prose-hiva prose-hiva-compact text-ink"
                dangerouslySetInnerHTML={{ __html: snapshotHtml }}
              />
              {isLong && !expanded ? (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface to-transparent"
                />
              ) : null}
            </div>
          )}
        </div>
      ) : null}
      {isLong && !showingDiff ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-brand hover:underline mt-1"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              {t('comments_thread.diff.collapse')}
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              {t('comments_thread.diff.expand')}
            </>
          )}
        </button>
      ) : null}
    </div>
  )
}

// ─── SnapshotDiffView (Doc 21 Fase 10) ──────────────────────────────────
/**
 * Render line-based del diff entre snapshot y texto vivo. Mismo
 * estilo visual que el `DiffView` del modal "Subsanar con IA" (Fase 7):
 * font-mono, fondos soft official/critical, line-through en deletes,
 * scroll interno con max-h-72. Mantenemos la consistencia visual para
 * que el reviewer reconozca el patrón.
 */
function SnapshotDiffView({
  diff,
}: {
  diff: ReturnType<typeof diffLines>
}) {
  return (
    <div className="max-h-72 overflow-y-auto rounded border border-line bg-canvas font-mono text-[11px] leading-snug">
      {diff.map((line, idx) => {
        const base = 'px-2 py-0.5 whitespace-pre-wrap break-words flex gap-2'
        if (line.type === 'add') {
          return (
            <div
              key={`add-${idx}`}
              className={`${base} bg-official-soft/60 text-official`}
            >
              <span className="select-none text-official/70 shrink-0">+</span>
              <span className="break-all">{line.text || ' '}</span>
            </div>
          )
        }
        if (line.type === 'del') {
          return (
            <div
              key={`del-${idx}`}
              className={`${base} bg-critical-soft/60 text-critical`}
            >
              <span className="select-none text-critical/70 shrink-0">−</span>
              <span className="break-all line-through opacity-80">
                {line.text || ' '}
              </span>
            </div>
          )
        }
        return (
          <div key={`eq-${idx}`} className={`${base} text-ink-muted`}>
            <span className="select-none text-ink-subtle/40 shrink-0">
              {' '}
            </span>
            <span className="break-all">{line.text || ' '}</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── StatusChip ─────────────────────────────────────────────────────────────
function StatusChip({ status }: { status: CommentStatus }) {
  const { t } = useTranslation('projects')
  const styles = {
    pending: 'bg-review-soft text-review',
    addressed: 'bg-brand-soft text-brand',
    resolved: 'bg-official-soft text-official',
  } as const
  return (
    <span
      className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-medium ${styles[status]}`}
    >
      {t(`comments_panel.status.${status}` as const)}
    </span>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatRelativeOrAbs(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.max(0, Math.floor((now - then) / 1000))
  if (diffSec < 60) return 'now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`
  // Después de 24h, fecha absoluta con formato del UI locale (es-MX, en-US).
  return new Date(iso).toLocaleDateString(toIntlLocale(i18n.language))
}

// ─── Helper: agrupa flat list de comments en threads (root + children). ─
export function groupCommentsByThread(
  comments: ArtifactComment[],
): CommentThreadGroup[] {
  // Roots ordenados cronológicamente ASC (más antiguo arriba, más nuevo
  // abajo). Sin este sort el orden quedaba a merced de lo que el backend
  // devolviera, lo que dejaba la lista no-determinista. Cronológico ASC
  // es el patrón estándar de threads (Slack/Gmail) y deja el último
  // comentario al fondo, donde el usuario hace scroll para ver lo
  // nuevo. Replies ya se ordenan ASC más abajo.
  const roots = comments
    .filter((c) => c.kind === 'comment')
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
  const childrenByParent = new Map<string, ArtifactComment[]>()
  for (const c of comments) {
    if (c.parent_comment_id) {
      const arr = childrenByParent.get(c.parent_comment_id) ?? []
      arr.push(c)
      childrenByParent.set(c.parent_comment_id, arr)
    }
  }
  return roots.map((root) => ({
    root,
    children: (childrenByParent.get(root.id) ?? []).sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    ),
  }))
}
